import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useIdempotentSend } from '@/lib/idempotent-send'
import { invalidateInventory } from '@/lib/inventory-invalidation'

/**
 * The stock write-off API (WP04). A write-off has no document of its own: the
 * list reads the stock ledger's damage, scrap and expiry movements. What a
 * write-off cost and whether the reader may make one are the server's
 * (`value`, `meta.abilities`); nothing here derives a rule. Keys live under
 * ['inventory', company, 'stock-disposals'], a family the invalidation map owns.
 */

export type Disposal = components['schemas']['StockDisposalResource']
export type DisposalRequest = components['schemas']['StockDisposalRequest']
export type DisposalType = 'damage' | 'scrap' | 'expiry'
export type WriteOffType = DisposalRequest['type']
export type WriteOffSource = NonNullable<DisposalRequest['stock_state']>

export const DISPOSAL_TYPES: DisposalType[] = ['damage', 'scrap', 'expiry']
export const WRITE_OFF_TYPES: WriteOffType[] = ['damage', 'scrap']
export const WRITE_OFF_SOURCES: WriteOffSource[] = ['available', 'damaged']

export type DisposalFilters = {
  type?: DisposalType
  warehouse?: string
  q?: string
  page?: number
  perPage?: number
}

export const disposalKeys = {
  all: (companyId: string | null) => ['inventory', companyId, 'stock-disposals'] as const,
  list: (companyId: string | null, filters: DisposalFilters) =>
    ['inventory', companyId, 'stock-disposals', 'list', filters] as const,
}

export function useDisposals(companyId: string | null, filters: DisposalFilters) {
  return useQuery({
    queryKey: disposalKeys.list(companyId, filters),
    enabled: Boolean(companyId),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.GET('/inventory/stock-disposals', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: {
          query: {
            type: filters.type,
            warehouse_id: filters.warehouse,
            search: filters.q,
            page: filters.page,
            per_page: filters.perPage ?? 25,
          },
        },
      })
      if (!response.data) throw new Error('GET /inventory/stock-disposals returned no body')
      return response.data
    },
  })
}

/** Writes stock off. The key comes from `useIdempotentSend`, so a retry after a lost answer destroys nothing twice. */
export function useWriteOff(companyId: string) {
  const queryClient = useQueryClient()
  const send = useIdempotentSend()

  return useMutation({
    mutationFn: async (input: { body: DisposalRequest }): Promise<Disposal> =>
      send(input, async (key) => {
        const response = await api.POST('/inventory/stock-disposals', {
          headers: { 'X-Company-Id': companyId, 'Idempotency-Key': key },
          body: input.body,
        })
        if (!response.data) throw new Error('POST /inventory/stock-disposals returned no body')
        return response.data.data
      }),
    onSuccess: () => invalidateInventory(queryClient, companyId, 'stock.moved'),
  })
}
